/**
 * Automation (Nova): typed client over the local `api` helper (M4.3).
 *
 * Mirrors the M4.1/M4.2 REST surface (every response is the `{data,error,meta}`
 * envelope). Shared DTOs come from `@tabbio/types`; do NOT use
 * `@tabbio/api-client` (dead). Nova is Pro-only: a non-Pro caller gets a
 * 403 SUBSCRIPTION_ENTITLEMENT_DENIED (branch on {@link isNovaEntitlementDenied}
 * to open the Nova upgrade card), and activating an automation whose connector
 * isn't connected yields a 409 NOVA_CONNECTOR_REQUIRED (branch on
 * {@link isNovaConnectorRequired} to show a connect-app prompt).
 */
import type {
  AgentArtifactSummary,
  AgentChatCapabilities,
  APIResponse,
  EntitlementDecision,
  NovaAutomation,
  NovaAutomationSummary,
  NovaCompileRequest,
  NovaCompileResult,
  NovaPendingApproval,
  NovaPlannedStep,
  NovaReportPreview,
  NovaRunDigest,
  NovaRunMatchItem,
  NovaRunSummary,
  NovaStructuredPreview,
  NovaTemplateInputs,
  NovaTemplateInputSpec,
} from '@tabbio/types';
import { api } from './api';

// ── Error codes the Nova flows branch on ─────────────────────────────────────

export const NOVA_ENTITLEMENT_DENIED = 'SUBSCRIPTION_ENTITLEMENT_DENIED';
export const NOVA_CONNECTOR_REQUIRED = 'NOVA_CONNECTOR_REQUIRED';
export const NOVA_RATE_LIMITED = 'NOVA_RATE_LIMITED';
/** 400 from templates/:key/enable: meta.field names the input to fix. */
export const NOVA_TEMPLATE_INPUT_INVALID = 'NOVA_TEMPLATE_INPUT_INVALID';

/** Entitlement decision attached to a 403 SUBSCRIPTION_ENTITLEMENT_DENIED. */
export type NovaEntitlementDecision = EntitlementDecision;

export class NovaApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'NovaApiError';
  }

  /** Entitlement decision for a 403 (drives the Nova upgrade card). */
  get decision(): NovaEntitlementDecision | undefined {
    return this.meta?.decision as NovaEntitlementDecision | undefined;
  }

  /** Connector slugs the automation needs (409 NOVA_CONNECTOR_REQUIRED). */
  get connectors(): string[] {
    const raw = this.meta?.connectors;
    return Array.isArray(raw) ? (raw as string[]) : [];
  }

  /** Seconds to wait before retrying (429 RATE_LIMITED). */
  get retryAfter(): number | undefined {
    const raw = this.meta?.retryAfter;
    return typeof raw === 'number' ? raw : undefined;
  }

  /** Which cap was hit (429): 'automation' | 'user' | 'concurrency'. */
  get scope(): 'automation' | 'user' | 'concurrency' | undefined {
    const raw = this.meta?.scope;
    return raw === 'automation' || raw === 'user' || raw === 'concurrency'
      ? raw
      : undefined;
  }
}

export function isNovaEntitlementDenied(error: unknown): error is NovaApiError {
  return error instanceof NovaApiError && error.code === NOVA_ENTITLEMENT_DENIED;
}

export function isNovaConnectorRequired(error: unknown): error is NovaApiError {
  return error instanceof NovaApiError && error.code === NOVA_CONNECTOR_REQUIRED;
}

export function isNovaRateLimited(error: unknown): error is NovaApiError {
  return error instanceof NovaApiError && error.code === NOVA_RATE_LIMITED;
}

/** A template input was missing or unusable: `meta.field` names it. */
export function isNovaTemplateInputError(
  error: unknown,
): error is NovaApiError {
  return (
    error instanceof NovaApiError && error.code === NOVA_TEMPLATE_INPUT_INVALID
  );
}

/** 409 NOVA_ACTIVE_LIMIT_REACHED: meta carries { active, max }. */
export function isNovaActiveLimit(error: unknown): error is NovaApiError {
  return (
    error instanceof NovaApiError &&
    error.code === 'NOVA_ACTIVE_LIMIT_REACHED'
  );
}

function unwrap<T>(res: APIResponse<T>): T {
  if (res.error) {
    throw new NovaApiError(
      res.error.message,
      res.error.code,
      res.meta as Record<string, unknown> | undefined,
    );
  }
  if (res.data == null) {
    throw new NovaApiError('Empty response', 'EMPTY_RESPONSE');
  }
  return res.data;
}

// ── Board (list / detail) ─────────────────────────────────────────────────────

/** GET /api/automation: the viewer's automation board. */
export async function listAutomations(): Promise<NovaAutomationSummary[]> {
  const res = unwrap(
    await api.get<{ automations: NovaAutomationSummary[] }>('/api/automation'),
  );
  return res.automations ?? [];
}

/** Full automation record for a single card (DSL + summary). */
export interface NovaAutomationDetail {
  automation: NovaAutomationSummary;
  dsl: NovaAutomation | null;
  planSummary?: string | null;
  /**
   * The server's plan preview. Its `report` (research plans: sources scope,
   * files, image, credits per run) is computed server side only.
   */
  structuredPreview?: NovaStructuredPreview | null;
}

/** GET /api/automation/:id: one automation with its active DSL. */
export async function getAutomation(
  id: string,
): Promise<NovaAutomationDetail> {
  return unwrap(await api.get<NovaAutomationDetail>(`/api/automation/${id}`));
}

// ── Preview normaliser (patch/get → NovaCompileResult) ────────────────────────

/**
 * The `PATCH /:id` (refine) endpoint returns the same `{ automation, dsl,
 * planSummary }` shape as `GET /:id`: NOT a compile result. The builder's
 * plan-preview panel wants a {@link NovaCompileResult}, so we derive the
 * structured preview from the DSL's per-step safety flags.
 */
export function structuredPreviewFromDsl(
  dsl: NovaAutomation,
): NovaStructuredPreview {
  return {
    trigger: { type: dsl.trigger.type, description: dsl.summary ?? '' },
    steps: dsl.steps.map((step) => ({
      id: step.id,
      type: step.type,
      label: step.type,
      dryRun: step.safety.dry_run,
      requiresApproval: step.safety.requires_approval,
      sideEffecting: step.safety.dry_run || step.safety.requires_approval,
    })),
    outputs: dsl.outputs.map((out) => ({
      id: out.id,
      type: out.type,
      description: out.destination ?? out.type,
    })),
    limits: dsl.limits,
  };
}

/** The research preview the server computed for a plan, when it has one. */
export function reportPreviewOf(
  preview: NovaStructuredPreview | null | undefined,
): NovaReportPreview | null {
  const report = preview?.report;
  return report && Array.isArray(report.sources) ? report : null;
}

function detailToCompileResult(detail: NovaAutomationDetail): NovaCompileResult {
  const dsl = detail.dsl as NovaAutomation;
  const result: NovaCompileResult = {
    automationId: detail.automation.id,
    versionId: detail.automation.activeVersionId ?? '',
    dsl,
    summary: detail.planSummary ?? dsl.summary ?? '',
    structuredPreview: structuredPreviewFromDsl(dsl),
  };
  // Sources scope, files, image and credits come from the server's preview.
  result.structuredPreview.report = reportPreviewOf(detail.structuredPreview);
  return result;
}

/**
 * GET /api/automation/:id for the builder: the saved plan of a DRAFT as a
 * {@link NovaCompileResult}, so a draft created elsewhere (an agent, a
 * template, an earlier session) opens with its plan and the Activate action
 * without recompiling. Null for anything that is not a draft with a plan.
 */
export async function getDraftPlanPreview(
  id: string,
): Promise<NovaCompileResult | null> {
  const detail = await getAutomation(id);
  if (detail.automation.status !== 'draft' || !detail.dsl) {
    return null;
  }
  return detailToCompileResult(detail);
}

// ── Compile (plain-English → plan) ────────────────────────────────────────────

/** POST /api/automation/compile: plain sentence → compiled plan preview. */
export async function compileAutomation(
  input: NovaCompileRequest,
): Promise<NovaCompileResult> {
  return unwrap(
    await api.post<NovaCompileResult>('/api/automation/compile', input),
  );
}

// ── Refine / safe-edit ────────────────────────────────────────────────────────

/**
 * PATCH /api/automation/:id: refine (a new plain sentence) or a structured
 * safe-edit patch. Returns the recompiled plan preview.
 */
export interface NovaRefinePatch {
  sentence?: string;
  locale?: string;
  scheduleHint?: string;
  name?: string;
  /** Structured safe-edit patch (schedule / limits) applied server-side. */
  patch?: Record<string, unknown>;
  /** The Research, Image and Result controls (same shape as chat). */
  capabilities?: AgentChatCapabilities;
  /** The device's IANA timezone. */
  timezone?: string;
}

export async function refineAutomation(
  id: string,
  patch: NovaRefinePatch,
): Promise<NovaCompileResult> {
  const detail = unwrap(
    await api.patch<NovaAutomationDetail>(`/api/automation/${id}`, patch),
  );
  return detailToCompileResult(detail);
}

// ── Lifecycle ──────────────────────────────────────────────────────────────────

/**
 * POST /api/automation/:id/activate: approve + activate a compiled plan.
 * Throws {@link NovaApiError} with code NOVA_CONNECTOR_REQUIRED (409) when a
 * needed connector isn't connected (branch → connect-app prompt).
 */
export async function activateAutomation(
  id: string,
): Promise<NovaAutomationSummary> {
  const res = unwrap(
    await api.post<{ automation: NovaAutomationSummary }>(
      `/api/automation/${id}/activate`,
      {},
    ),
  );
  return res.automation;
}

export async function pauseAutomation(
  id: string,
): Promise<NovaAutomationSummary> {
  const res = unwrap(
    await api.post<{ automation: NovaAutomationSummary }>(
      `/api/automation/${id}/pause`,
      {},
    ),
  );
  return res.automation;
}

export async function resumeAutomation(
  id: string,
): Promise<NovaAutomationSummary> {
  const res = unwrap(
    await api.post<{ automation: NovaAutomationSummary }>(
      `/api/automation/${id}/resume`,
      {},
    ),
  );
  return res.automation;
}

export async function deleteAutomation(id: string): Promise<void> {
  const res = await api.delete<{ deleted: boolean }>(`/api/automation/${id}`);
  if (res.error) {
    throw new NovaApiError(
      res.error.message,
      res.error.code,
      res.meta as Record<string, unknown> | undefined,
    );
  }
}

/** POST /api/automation/:id/run-now: kick off an out-of-schedule run. */
export async function runNow(id: string): Promise<NovaRunSummary> {
  const res = unwrap(
    await api.post<{ run: NovaRunSummary }>(
      `/api/automation/${id}/run-now`,
      {},
    ),
  );
  return res.run;
}

// ── Run history ────────────────────────────────────────────────────────────────

export interface NovaRunListResult {
  runs: NovaRunSummary[];
  nextCursor?: string | null;
}

export interface ListRunsQuery {
  status?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

/** GET /api/automation/:id/runs: run history for one automation. */
export async function listRuns(
  automationId: string,
  query: ListRunsQuery = {},
): Promise<NovaRunListResult> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  if (query.limit) params.set('limit', String(query.limit));
  if (query.cursor) params.set('cursor', query.cursor);
  const qs = params.toString();
  return unwrap(
    await api.get<NovaRunListResult>(
      `/api/automation/${automationId}/runs${qs ? `?${qs}` : ''}`,
    ),
  );
}

/** One executed (or executing) step of a run, with its raw output. */
export interface NovaRunStepDetail {
  id: string;
  /** The DSL step id: joins this row to the run's plannedSteps. */
  stepId?: string;
  type: string;
  status: string;
  dryRun?: boolean;
  startTime?: string | Date | null;
  endTime?: string | Date | null;
  error?: string | null;
  /** Raw step output: search matches / digest / draft handle / summary. */
  output?: Record<string, unknown> | null;
}

/**
 * Detailed run record: matches the ACTUAL server shape of
 * GET /api/automation/runs/:runId: `{ run, plannedSteps, steps }`.
 * `plannedSteps` is the full plan from the run's version, so a live view can
 * render the whole checklist before pending steps have rows.
 */
export interface NovaRunDetail {
  run: NovaRunSummary;
  plannedSteps?: NovaPlannedStep[];
  steps?: NovaRunStepDetail[];
  /** Files the run produced (Tasks parity); absent on older API builds. */
  artifacts?: AgentArtifactSummary[];
}

/** GET /api/automation/runs/:runId: a single run with its planned + run steps. */
export async function getRun(runId: string): Promise<NovaRunDetail> {
  return unwrap(await api.get<NovaRunDetail>(`/api/automation/runs/${runId}`));
}

export type { NovaPendingApproval } from '@tabbio/types';

export interface NovaStats {
  activeCount: number;
  activeMax: number | null;
}

/** GET /api/automation/stats: active count vs the plan ceiling (board meter). */
export async function getAutomationStats(): Promise<NovaStats> {
  return unwrap(await api.get<NovaStats>('/api/automation/stats'));
}

/** GET /api/automation/approvals: every run awaiting the user's approval. */
export async function listPendingApprovals(): Promise<NovaPendingApproval[]> {
  const res = unwrap(
    await api.get<{ approvals: NovaPendingApproval[] }>(
      '/api/automation/approvals',
    ),
  );
  return res.approvals;
}

export async function approveRun(runId: string): Promise<NovaRunSummary> {
  const res = unwrap(
    await api.post<{ run: NovaRunSummary }>(
      `/api/automation/runs/${runId}/approve`,
      {},
    ),
  );
  return res.run;
}

export async function rejectRun(runId: string): Promise<NovaRunSummary> {
  const res = unwrap(
    await api.post<{ run: NovaRunSummary }>(
      `/api/automation/runs/${runId}/reject`,
      {},
    ),
  );
  return res.run;
}

// ── Templates ──────────────────────────────────────────────────────────────────

export interface NovaTemplateSummary {
  key: string;
  title: string;
  description?: string | null;
  category?: string | null;
  status?: string | null;
  /** Connector slugs this template needs (for the connect-app prompt). */
  requiredConnectors?: string[];
  /** What the user fills in before enabling (topic, link, PDF). */
  inputs?: NovaTemplateInputSpec[];
  /** Reads the web or the user's sources. */
  research?: boolean;
}

/** GET /api/automation/templates: the "start from a template" row. */
export async function listTemplates(): Promise<NovaTemplateSummary[]> {
  const res = unwrap(
    await api.get<{ templates: NovaTemplateSummary[] }>(
      '/api/automation/templates',
    ),
  );
  return res.templates ?? [];
}

export interface NovaTemplateEnableOptions {
  /** Validated per template on the server (topic, link, owned PDF). */
  inputs?: NovaTemplateInputs;
  /** The device's IANA timezone for the schedule. */
  timezone?: string;
  /** The report language. */
  locale?: string;
}

/** POST /api/automation/templates/:key/enable: clone a template onto the board. */
export async function enableTemplate(
  key: string,
  options: NovaTemplateEnableOptions = {},
): Promise<NovaAutomationSummary> {
  const res = unwrap(
    await api.post<{ automation: NovaAutomationSummary }>(
      `/api/automation/templates/${key}/enable`,
      options,
    ),
  );
  return res.automation;
}

/** One of the user's uploaded PDFs, for a template's PDF picker. */
export interface NovaPdfAsset {
  id: string;
  title: string;
  createdAt?: string | null;
}

/**
 * GET /api/agent/assets filtered to PDFs: the files a "Read this PDF" task can
 * read. Ids only leave this device; the server re-checks ownership.
 */
export async function listPdfAssets(): Promise<NovaPdfAsset[]> {
  const res = await api.get<unknown>('/api/agent/assets');
  if (res.error) {
    throw new NovaApiError(res.error.message, res.error.code);
  }
  const rows = Array.isArray(res.data) ? res.data : [];
  const out: NovaPdfAsset[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const record = row as Record<string, unknown>;
    if (record.mimeType !== 'application/pdf' || typeof record.id !== 'string') {
      continue;
    }
    out.push({
      id: record.id,
      title:
        typeof record.title === 'string' && record.title.trim()
          ? record.title.trim()
          : 'PDF',
      createdAt: typeof record.createdAt === 'string' ? record.createdAt : null,
    });
  }
  return out;
}

// ── Re-exports ──────────────────────────────────────────────────────────────────

export type {
  NovaAutomation,
  NovaAutomationSummary,
  NovaCompileResult,
  NovaPlannedStep,
  NovaRunDigest,
  NovaRunMatchItem,
  NovaRunSummary,
};
